import React, { memo, useMemo } from 'react';
import Markdown, { MarkdownToJSX } from "markdown-to-jsx";
import { ContactTag, UserTag } from "./MessageTags";
import ChatGenericTag from './ChatGenericTag';
import { DepartmentTagByUuid } from '../../../components/buttons';

interface Props {
    children?: string | null
    plain?: boolean
}

interface CustomAProps extends HTMLAnchorElement {
    children: any
}

export const userTagRegex = /<UserTag uuid="(.*?)" \/>/gm;
export const contactTagRegex = /<ContactTag contact={(.*?)}\/>/gm;
export const departmentTagRegex = /<DepartmentTag uuid="(.*?)" \/>/gm;
export const genericTagRegex = /\|~(.*?)~\|/gm;

const tagParse = (str: string): string => str
    .replace(userTagRegex, (_, uuid) => `[*%$User Tag$%*](${uuid})`)
    .replace(contactTagRegex, (_, contact) => `[*%$Contact Tag$%*](${contact})`)
    .replace(departmentTagRegex, (_, uuid) => `[*%$Department Tag$%*](${uuid})`)
    .replace(genericTagRegex, (_, val) => `[*%$Generic Tag$%*](${val})`);

const HeaderOverride = (props: any) => <p>#{props.children}</p>;
const HideBr = () => <>&nbsp;</>;
const TextOnly = (props: any) => <>{props.children}</>;

const CustomCode: React.FC<{ className: string | undefined, children: string | string[] }> = (props) =>
    props.className || Array.isArray(props.children) ? (
        <pre><code>{props.children}</code></pre>
    ) : (
        <code>{props.children}</code>
    );

export const MarkdownContainer: React.FC<Props> = memo(({ children, plain }) => {
    const CustomA = useMemo(() => (props: CustomAProps) => {
        if (props.children[0]?.props?.children?.[0] === '%$User Tag$%') {
            return (
                <span>
                    <UserTag key={props.href} tag={{ uuid: props.href }} plain={plain} />
                </span>
            );
        }
        if (props.children[0]?.props?.children?.[0] === '%$Department Tag$%') {
            return (
                <span>
                    <span><DepartmentTagByUuid uuid={props.href} /></span>
                </span>
            );
        }
        if (props.children[0]?.props?.children?.[0] === '%$Contact Tag$%') {
            return (
                <span>
                    <ContactTag tag={{ contact: props.href }} plain={plain} />
                </span>
            );
        }
        if (props.children[0]?.props?.children?.[0] === '%$Generic Tag$%') {
            const [text, colour] = props.href.split(':');
            return <ChatGenericTag text={text} colour_raw={colour} plain={plain} />;
        }
        if (plain) return <>{props.children}</>;
        return (
            <a href={props.href} title={props.href} target='_blank' rel='noreferrer'>
                {props.children}
            </a>
        );
    }, [plain]);

    const options = useMemo(() => ({
        forceInline: plain || false,
        wrapper: plain ? TextOnly : undefined,
        forceWrapper: plain,
        overrides: {
            a: { component: CustomA },
            this: { component: TextOnly },
            br: { component: plain ? HideBr : 'br' },
            span: { component: plain ? TextOnly : 'p' },
            p: { component: plain ? TextOnly : 'p' },
            h1: HeaderOverride,
            div: { component: TextOnly },
            code: { component: plain ? 'code' : CustomCode },
            yTag: { component: 'p' },
        },
        disableParsingRawHTML: true,
    }) as MarkdownToJSX.Options, [plain, CustomA]);

    if (!children) return null;

    return (
        <Markdown options={options}>
            {tagParse(children)}
        </Markdown>
    );
});
